import CloudUploadIcon from '@mdi/svg/svg/cloud-upload.svg?react';
import ReceiptIcon from '@mdi/svg/svg/receipt.svg?react';
import StarIcon from '@mdi/svg/svg/star.svg?react';
import { type FutureEventEntity } from '@solink/vd-data-layer/api-clients/platform/platform';
import { useAgentPermissions } from '@solinkcorp/agent/AgentsPage/hooks/useAgentPermissions';
import { type DeviceEntity, type EventFlow, type SiteId } from '@solinkcorp/api-types';
import {
  EVENT_LIST_LAYOUT_TYPES,
  type EventListLayoutType,
  type JWTUser,
  type UserPreferencesWithDefaults,
  useUserHasFeature,
  useUserPreferences,
  useUserSession,
} from '@solinkcorp/authentication';
import { type LocaleDefinition } from '@solinkcorp/locale';
import { Button, Flex, Grid, MDIIcon, TextArea, toast } from '@solinkcorp/solink-components';
import { type HistogramRange, type SearchOperator } from '@solinkcorp/vd-common';
import type * as ContextineActions from 'actions/ContextineActions';
import { setCurrentScrollEvent } from 'actions/currentScrollEventActions';
import EventActions from 'actions/EventActions';
import EventItemActions from 'actions/EventItemActions';
import classNames from 'classnames';
import { type IEvent } from 'clients/PlatformApi';
import AlarmEventActionsRenderer from 'components/AlarmsPage/AlarmEventActionsRenderer';
import { CommonContext } from 'components/AuthenticatedApp/AuthenticatedApp';
import AutoTitle from 'components/AutoTitle';
import { type PlayEventHandler } from 'components/EventPlayer/types';
import EventIcon from 'components/EventsPage/EventIcon';
import { usePlayingCamerasContext } from 'components/PlayingCamerasContext';
import {
  type CancelScheduledEventType,
  useScheduledEventForm,
} from 'components/ScheduledActivityForm/useScheduledEventForm';
import {
  type CameraIdentifier,
  type PlayingCamera,
} from 'components/VideoPlayer/controller/PlayingCamerasController';
import ThemeVariables from 'env/themeVariables';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import AnalyticsLogger from 'models/AnalyticsLogger';
import React, { Component, forwardRef, useCallback, useContext, useRef } from 'react';
import { useControllerState } from 'solox';
import { isKiosk } from 'src/core/utils/kioskUtils';
import useDevices from 'src/hooks/devices/useDevices';
import { useCurrentFeature } from 'src/hooks/useEventFeature';
import useFluxStore from 'src/hooks/useFluxStore';
import useIsElementFocused from 'src/hooks/useIsElementFocused';
import useIsElementScrollable from 'src/hooks/useIsElementScrollable';
import useLocale from 'src/hooks/useLocale';
import { LocationStore } from 'stores/LocationStore';
import { type DispatchType, useDispatch, useSelector, useStore, type VDStore } from 'stores/store';
import { getColorForEvent } from 'styles/eventColors';
import { a11yKey } from 'utils/ComponentUtil';
import { buildEventSearchUrls } from 'utils/eventSearchUrls';
import * as EventUtil from 'utils/EventUtil';
import FeaturesUtil from 'utils/FeaturesUtil';
import isShallowEqual from 'utils/isShallowEqual';
import LocaleUtil from 'utils/LocaleUtil';
import { type S3 } from 'utils/S3';
import SystemReport from 'utils/SystemReport';
import {
  type CamerasDictionary,
  type EventFilterState,
  type VideoDiscoverySite,
} from 'utils/types';
import { DEFAULT_TIMEZONE, FAST_NOTIFICATION_DURATION } from 'vd-constants';
import EventDetails from '../EventDetails';
import { type FieldValue } from '../renderers/types';
import AgentFindings from './AgentFindings/AgentFindings';
import { EVENT_TYPE_WIDTH } from './constant';
import DataSource from './DataSource';
import './Event.sass';
import EventActionsRow from './EventActionsRow';
import EventThumbnails from './EventThumbnails';
import EventTimeLocation from './EventTimeLocation';
import EventVerify from './EventVerify/EventVerify';
import EventVideos from './EventVideos';
import SidekickAnswers from './SidekickAnswers/SidekickAnswers';
import useEventFlow from './useEventFlow';

export const descriptionMaxLength = 300;
export const TITLE_LINE_GAP = 4;

function getEventCardGridTemplateRows({
  isPreview,
  isVerifyVisible,
  isAlarmActionsVisible,
  isSidekickAnswersVisible,
  isAgentFindingsVisible,
  eventHasAgentFindings,
}: {
  isPreview?: boolean;
  isVerifyVisible: boolean;
  isAlarmActionsVisible?: boolean;
  isSidekickAnswersVisible?: boolean;
  isAgentFindingsVisible?: boolean;
  eventHasAgentFindings?: boolean;
}) {
  const gridTemplateRows = ['auto'];

  // Add an extra row if not in preview mode because the preview mode doesn't
  // show the event thumbnail
  if (!isPreview) gridTemplateRows.push('auto');

  // Add an extra row if the verify section is visible
  if (isVerifyVisible) gridTemplateRows.push('auto');

  // Add an extra row if the verify section is visible
  if (isSidekickAnswersVisible) gridTemplateRows.push('auto');

  if (isAgentFindingsVisible && eventHasAgentFindings) gridTemplateRows.push('auto');

  // Always have a row for event content
  gridTemplateRows.push('1fr');

  // Add a row for alarm actions if they are visible
  if (isAlarmActionsVisible) gridTemplateRows.push('auto');

  return gridTemplateRows.join(' ');
}

export interface CommonEventProps {
  /** True if this list is being viewed from the alarms page. */
  isAlarms?: boolean;
  /** True if this list is being viewed from the clips page. */
  isClips?: boolean;
  hideActionMenu?: boolean;
  /** True if this list is being viewed from the events page. */
  isEvents?: boolean;
  /** True if the video player is open. */
  isVideoPlayerOpen?: boolean;
  /** True if it's expanded. Only applies when the player is open. */
  isExpanded?: boolean;
  /** The current event filter state. */
  eventFilters: EventFilterState;
  /** The currently selected report. */
  selectedFilterId?: string;
  selectedReportName?: string;
  /** The current search string. */
  searchString?: string;
  searchStringRegex?: RegExp;
  /** The current search capsule list. */
  searchCapsules?: readonly string[];
  searchOperator?: SearchOperator;
  showRevenue?: boolean;
  active?: boolean;
  /** Collapse the event card in video player */
  collapseCard?: () => void;
}

export interface EventProps extends CommonEventProps {
  event: IEvent;
  colorMode?: 'light' | 'dark';
  onEventRowClick?: PlayEventHandler;
  updateEvent?: (newEvent: IEvent) => void;
  removeEvent?: (targetEvent: IEvent) => void;
  layoutType?: EventListLayoutType;
  style?: React.CSSProperties;
  /** True if display the preview mode of the event. Hide thumbnail and actions. */
  isPreview?: boolean;
  /** List of workflows that events can be reviewed with. */
  workflows?: EventFlow[];
}

export interface InnerEventProps extends EventProps {
  forwardedRef: React.Ref<HTMLDivElement>;
  contextineCreds: ContextineActions.ContextineCredentials | undefined;
  contextineEnabled: boolean;
  dispatch: DispatchType;
  filterDate: number;
  locale: LocaleDefinition;
  store: VDStore;
  user: JWTUser | undefined;
  tenantId: string;
  updateContextMenuStatus: (params: { isOpen: boolean }) => void;
  playingCameras: PlayingCamera[];
  playingCameraIds: readonly CameraIdentifier[];
  histogramRange: HistogramRange;
  isContextMenuOpen: boolean;
  isLoadingLocations: boolean;
  location: VideoDiscoverySite | undefined;
  s3?: S3;
  selectedLocationIds: readonly SiteId[] | undefined;
  selectedLocationsTimezoneId: string;
  currentScrollEventId: string | undefined;
  cancelScheduledEvent: CancelScheduledEventType;
  camerasDictionary: CamerasDictionary;
  reopenScheduledEvent: (updateEvent: (newEvent: IEvent) => void) => void;
  isLoadingScheduledEvent?: boolean;
  feature: string | undefined;
  preferences: UserPreferencesWithDefaults;
  observeEventContent: (element?: HTMLDivElement | null | undefined) => void;
  isEventContentScrollable: boolean;
  setEditNotesTextAreaRef: (ReactNode: HTMLTextAreaElement) => void;
  editNotesTextAreaRef: React.MutableRefObject<HTMLTextAreaElement | null>;
  isEditNotesTextAreaFocused: boolean;
  verificationWorkflow: EventFlow | undefined;
  devicesBySiteId: Record<string, DeviceEntity[]>;
  canViewAgentFindings: boolean;
  limitedAgentFindingsView?: boolean;
}

interface State {
  editMode?: boolean;
  // TODO: Why are all these features of the event duplicated in state?
  eventTitle: string | undefined;
  eventTitleOriginal: string | undefined;
  eventDescription: string;
  eventDescriptionOriginal: string;
  receipt: string;
  favourite: boolean;
  favouritedBy: string[];
  lastUpdatedBy: string | undefined;
  lastUpdateTime: string | undefined;
  subtitle: string;
  slideIndex: number;
  isAlarmOpen?: boolean;
  isLoading?: boolean;
  isVerifyVisible: boolean;
  isSidekickAnswersVisible: boolean;
  isAgentFindingsVisible: boolean;
}

class Event extends Component<InnerEventProps, State> {
  constructor(props: InnerEventProps) {
    super(props);
    const { event } = props;

    const eventDetails = event.details;

    //
    // TO DO: This doesn't make sense... we need something like
    //   AlarmEvent extends BaseEvent
    // So state like this only belongs to AlarmEvent
    //
    const isAlarmOpen = EventUtil.isAlarm(event) && EventUtil.isOpen(event);

    // TODO: Review whether we can clean-up the event title use
    this.state = {
      eventTitle: event.title,
      eventTitleOriginal: event.title,
      eventDescription: eventDetails?.description || '',
      eventDescriptionOriginal: eventDetails?.description || '',
      receipt: eventDetails?.Receipt || '',
      favourite: eventDetails?.favourite || false,
      favouritedBy: eventDetails?.favouritedBy || [],
      lastUpdatedBy: eventDetails?.lastUpdatedBy,
      lastUpdateTime: eventDetails?.lastUpdateTime,
      subtitle: event.subtitle || '',
      slideIndex: 0,
      isAlarmOpen,
      isVerifyVisible: false,
      isSidekickAnswersVisible: false,
      isAgentFindingsVisible: false,
    };
  }

  componentWillUnmount() {
    // Remove the listener for the context menu dismissal
    this._dismissContextMenu();
  }

  shouldComponentUpdate(nextProps: InnerEventProps, nextState: State) {
    const {
      currentScrollEventId,
      searchString,
      active,
      event,
      isLoadingLocations,
      onEventRowClick,
      playingCameraIds,
      isLoadingScheduledEvent,
      style,
      observeEventContent,
      isEventContentScrollable,
      isEditNotesTextAreaFocused,
      editNotesTextAreaRef,
      setEditNotesTextAreaRef,
      verificationWorkflow,
    } = this.props;
    const {
      editMode,
      eventTitle,
      eventDescription,
      favourite,
      slideIndex,
      isAlarmOpen,
      isVerifyVisible,
      isSidekickAnswersVisible,
      isAgentFindingsVisible,
    } = this.state;

    if (
      searchString !== nextProps.searchString ||
      active !== nextProps.active ||
      currentScrollEventId !== nextProps.currentScrollEventId ||
      onEventRowClick !== nextProps.onEventRowClick ||
      isLoadingScheduledEvent !== nextProps.isLoadingScheduledEvent ||
      style !== nextProps.style ||
      observeEventContent !== nextProps.observeEventContent ||
      isEventContentScrollable !== nextProps.isEventContentScrollable ||
      editMode !== nextState.editMode ||
      eventTitle !== nextState.eventTitle ||
      eventDescription !== nextState.eventDescription ||
      favourite !== nextState.favourite ||
      slideIndex !== nextState.slideIndex ||
      !isEqual(event, nextProps.event) ||
      isLoadingLocations !== nextProps.isLoadingLocations ||
      get(location, 'id') !== get(nextState, 'location.id') ||
      playingCameraIds.length !== nextProps.playingCameraIds.length ||
      (playingCameraIds.length > 0 &&
        nextProps.playingCameraIds.length > 0 &&
        playingCameraIds[0].chCameraId !== nextProps.playingCameraIds[0].chCameraId) ||
      isAlarmOpen !== nextState.isAlarmOpen ||
      isEditNotesTextAreaFocused !== nextProps.isEditNotesTextAreaFocused ||
      editNotesTextAreaRef !== nextProps.editNotesTextAreaRef ||
      setEditNotesTextAreaRef !== nextProps.setEditNotesTextAreaRef ||
      isVerifyVisible !== nextState.isVerifyVisible ||
      verificationWorkflow !== nextProps.verificationWorkflow ||
      isSidekickAnswersVisible !== nextState.isSidekickAnswersVisible ||
      isAgentFindingsVisible !== nextState.isAgentFindingsVisible
    ) {
      return true;
    }
    return false;
  }

  componentDidUpdate(prevProps: InnerEventProps, prevState: State) {
    const { event, isEditNotesTextAreaFocused, dispatch } = this.props;
    const { eventDescription, editMode } = this.state;
    const { event: prevEvent, isEditNotesTextAreaFocused: prevIsEditNotesTextAreaFocused } =
      prevProps;

    let savedDescription = eventDescription;

    // If the event changed, update event-dependent state properties
    if (prevEvent.id !== event.id) {
      savedDescription = '';
    }

    const eventDetails = event.details;
    const eventTitle = event.title;

    // Event updates from e.g. push-to-cloud
    if (
      eventTitle !== prevProps.event.title ||
      eventDetails?.description !== prevProps.event.details?.description
    ) {
      this.setState({
        eventTitle,
        eventDescription: savedDescription || eventDetails?.description || '',
      });
    }

    if (event.details?.favourite !== prevProps.event.details?.favourite) {
      this.setState({
        favourite: event.details?.favourite || false,
      });
    }

    if (isEditNotesTextAreaFocused && !prevIsEditNotesTextAreaFocused) {
      dispatch(setCurrentScrollEvent(event.id));
    }

    if (editMode && !prevState.editMode) {
      this.focusEditNotesTextArea();
    }
  }

  toggleVerifySection = () => {
    this.setState({
      isVerifyVisible: !this.state.isVerifyVisible,
      isSidekickAnswersVisible: false,
      isAgentFindingsVisible: false,
    });
  };
  toggleEventSpotCheckSection = () => {
    this.setState({
      isVerifyVisible: false,
      isSidekickAnswersVisible: !this.state.isSidekickAnswersVisible,
      isAgentFindingsVisible: false,
    });
  };
  toggleAgentFindingsSection = () => {
    this.setState({
      isVerifyVisible: false,
      isSidekickAnswersVisible: false,
      isAgentFindingsVisible: !this.state.isAgentFindingsVisible,
    });
  };

  render() {
    const {
      active,
      colorMode,
      currentScrollEventId,
      event,
      isLoadingLocations,
      hideActionMenu,
      isAlarms,
      isVideoPlayerOpen,
      isPreview,
      locale,
      layoutType,
      location,
      selectedLocationsTimezoneId,
      observeEventContent,
      isEventContentScrollable,
      setEditNotesTextAreaRef,
      isExpanded,
      collapseCard,
      verificationWorkflow,
      updateEvent,
    } = this.props;
    const {
      eventDescription,
      editMode,
      eventTitle,
      isAlarmOpen,
      subtitle,
      isVerifyVisible,
      isSidekickAnswersVisible,
      isAgentFindingsVisible,
    } = this.state;

    // We can't allow user to edit events in certain cases.
    // Like when event id contains URL reserved characters,
    // we might not be able to get data or edit the event through `/events/{id}` API.
    const allowEdit = !EventUtil.isEventIdInvalid(event.id);
    const isAlarm = EventUtil.isAlarm(event);
    const isScheduledAIChat = EventUtil.isScheduledAIChatEvent(event);
    const isEventSpotCheck = EventUtil.isEventSpotCheckEvent(event);
    const { canViewAgentFindings } = this.props;
    const eventHasAgentFindings = canViewAgentFindings && EventUtil.hasAgentFindings(event);
    const isPeekLayout = layoutType === EVENT_LIST_LAYOUT_TYPES.PEEK;

    const isContentOverflowing = isPeekLayout && isEventContentScrollable;

    const isContentScrollable = isContentOverflowing && currentScrollEventId === event.id;

    const shouldShowThumbnail = !isPreview;
    const shouldShowEventActions = !isPreview;
    const isExpandedInVideoPlayer = isVideoPlayerOpen && isExpanded;

    const listItemClass = classNames('grid-item', 'event-card', colorMode, {
      'loading-locations': isLoadingLocations,
      alarm: isAlarm,
      'alarm-open': isAlarmOpen,
      'equal-height': isPeekLayout,
      'in-player': isVideoPlayerOpen,
      'scheduled-ai-chat': isScheduledAIChat,
    });

    const contentContainerClass = classNames('event-content-container', event.type, {
      scrollable: isContentScrollable,
    });

    const favouriteClass = this._getFavouriteClass();

    // Cannot delete if it is a shared or download bookmark
    const itemWidth = ThemeVariables.eventGridItemWidth;

    const shouldRenderDownload = EventUtil.isDownloadBookmark(event) && FeaturesUtil.canDownload();
    const shouldRenderDownloadTitle = shouldRenderDownload && eventTitle;
    const shouldRenderDescription =
      !editMode && (eventDescription.length > 0 || shouldRenderDownloadTitle);

    const locationTimezone = location?.timezone || DEFAULT_TIMEZONE;

    const descriptionEditInfo = LocaleUtil.getLocalizedStr({
      string: locale.fields.charactersAvailable,
      replacements: { count: descriptionMaxLength - eventDescription.length },
    });
    const hasDescriptionError = descriptionMaxLength < eventDescription.length;
    const eventColor = getColorForEvent(event, false);

    const descriptionEditClass = classNames('description-edit', { hidden: !editMode });

    const titleTimeLocationWrapperClass = classNames('title-time-location-wrapper', {
      clickable: isExpandedInVideoPlayer,
    });

    const isAlarmActionsVisible =
      !isVideoPlayerOpen && isAlarmOpen && isAlarms && location && !isPreview;

    const isEventVerified = event.details?.verificationConclusion;
    const shouldDisplayVerifySection = isVerifyVisible && (verificationWorkflow || isEventVerified);

    const gridTemplateRows = getEventCardGridTemplateRows({
      isPreview,
      isVerifyVisible,
      isAlarmActionsVisible,
      isSidekickAnswersVisible,
      isAgentFindingsVisible,
      eventHasAgentFindings,
    });

    const sidekickAnswers = event?.details?.scheduledActivity?.metadata?.aiChatAnswers;

    return (
      <Grid
        className={listItemClass}
        data-testid="events-cards"
        id={`event-${event.id}`}
        key={event.id}
        onMouseEnter={this._handleMouseHover}
        ref={this.props.forwardedRef}
        width={`${itemWidth}px`}
        gridTemplateRows={gridTemplateRows}
        gridTemplateColumns="100%"
        style={this.props.style}
      >
        {shouldShowThumbnail && (
          <div className="event-thumbnail-wrapper">
            <EventThumbnails
              event={event}
              active={active}
              onPlayEvent={this._onPlayEvent}
              playingCameraIds={this.props.playingCameraIds}
              width={itemWidth}
              isVideoPlayerOpen={isVideoPlayerOpen}
            />
            {allowEdit && (
              <div className="event-fav" data-testid="events-fav">
                <span
                  className={favouriteClass}
                  tabIndex={0}
                  onClick={this._toggleEventFavourite}
                  onKeyDown={a11yKey(this._toggleEventFavourite)}
                >
                  <MDIIcon icon={StarIcon} />
                </span>
              </div>
            )}
          </div>
        )}
        <Grid
          className="event-title-wrapper"
          gridTemplateColumns={`${EVENT_TYPE_WIDTH}px 1fr`}
          style={{ borderBottomColor: eventColor }}
        >
          <Flex
            className="event-type"
            justifyContent="center"
            alignItems="center"
            style={{ background: eventColor }}
          >
            <EventIcon
              isFutureEvent
              state={event.state}
              subtype={event.subtype}
              type={event.type}
            />
          </Flex>
          <Grid
            className="title-info-wrapper"
            gridTemplateColumns="1fr"
            gridTemplateRows="auto auto"
            gap={`${TITLE_LINE_GAP}px 0`}
          >
            <Flex justifyContent="space-between" flexDirection="row" alignItems="center">
              <DataSource subtitle={subtitle} dataSource={event.dataSource} />
              {shouldShowEventActions && (
                <EventActionsRow
                  allowEdit={allowEdit}
                  closeScheduledEvent={this._closeScheduledEvent}
                  colorMode={colorMode}
                  enterEditMode={this.enterEditMode}
                  event={event}
                  eventColor={eventColor}
                  eventDescription={eventDescription}
                  favourite={this.state.favourite}
                  hideActionMenu={hideActionMenu}
                  isAlarms={this.props.isAlarms}
                  isClips={this.props.isClips}
                  removeEvent={this.props.removeEvent}
                  reopenScheduledEvent={this._reopenScheduledEvent}
                  tenantId={this.props.tenantId}
                  toggleEventFavouriteMenu={this.toggleEventFavouriteMenu}
                  updateEvent={this.props.updateEvent}
                  toggleVerifySection={this.toggleVerifySection}
                  verificationWorkflow={verificationWorkflow}
                  isEventSpotCheck={isEventSpotCheck}
                  toggleEventSpotCheckSection={this.toggleEventSpotCheckSection}
                  hasAgentFindings={eventHasAgentFindings}
                  toggleAgentFindingsSection={this.toggleAgentFindingsSection}
                />
              )}
            </Flex>
            <div
              className={titleTimeLocationWrapperClass}
              onClick={isExpandedInVideoPlayer ? collapseCard : undefined}
            >
              <AutoTitle className="event-title" data-testid="event-text">
                {EventUtil.getTitle(event, locale)}
              </AutoTitle>
              <EventTimeLocation
                event={event}
                selectedLocationsTimezoneId={selectedLocationsTimezoneId}
                locationTimezone={locationTimezone}
                location={location}
                colorMode={colorMode}
              />
            </div>
          </Grid>
        </Grid>
        {isSidekickAnswersVisible && (
          <SidekickAnswers
            eventColor={eventColor}
            event={event}
            sidekickAnswers={sidekickAnswers}
          />
        )}
        {isAgentFindingsVisible && eventHasAgentFindings && (
          <AgentFindings
            eventColor={eventColor}
            event={event}
            limitedView={this.props.limitedAgentFindingsView}
          />
        )}
        {shouldDisplayVerifySection && updateEvent && (
          <EventVerify
            eventColor={eventColor}
            eventId={event.id}
            workflowIdOnEvent={event.details?.eventFlowId}
            workflow={verificationWorkflow}
            updateEvent={updateEvent}
            conclusion={event.details?.verificationConclusion}
            conclusionReason={event.details?.verificationConclusionReason}
          />
        )}
        <Flex
          className={contentContainerClass}
          alignItems="center"
          flexDirection="column"
          justifyContent="flex-start"
        >
          <Flex
            className="scrollable-event-content-wrap"
            flexDirection="column"
            ref={observeEventContent}
          >
            {shouldRenderDescription && (
              <div className="description" data-testid="event-notes">
                {!editMode &&
                  this._renderDescription({
                    shouldRenderDownloadTitle,
                  })}
              </div>
            )}
            <div className={descriptionEditClass}>
              <TextArea
                dataTestId="events-notes"
                colorMode={colorMode}
                errorText={hasDescriptionError ? descriptionEditInfo : ''}
                hintText={descriptionEditInfo}
                labelText={locale.event.eventDescription}
                name={`description-edit-field-${event.id}`}
                onChange={this.updateEventDescription}
                value={eventDescription}
                refFunc={setEditNotesTextAreaRef}
              />
              <Flex alignItems="center" className="edit-buttons-wrapper" justifyContent="flex-end">
                <Button
                  dataTestId="events-notes-cancel"
                  isPrimary={false}
                  onClick={this.cancelEdit}
                  size="thin"
                  text={locale.general.cancel}
                />
                <Button
                  dataTestId="events-notes-ok"
                  disabled={hasDescriptionError}
                  onClick={this.submitEdit}
                  size="thin"
                  text={locale.general.ok}
                  tooltip={hasDescriptionError ? locale.event.eventDescriptionError : ''}
                />
              </Flex>
            </div>
            {((EventUtil.isBookmark(event) && !EventUtil.isAlarm(event)) ||
              EventUtil.isShared(event)) &&
              this._renderUserDetails()}
            {this._renderReceipt(locationTimezone)}
          </Flex>
          {isContentOverflowing && (
            <Flex
              alignItems="center"
              data-testid="events-content-scroll-enabler"
              className="content-scroll-enabler"
              justifyContent="center"
              onClick={this.setCurrentScrollEvent}
            >
              <div>{locale.event.enableScroll}</div>
            </Flex>
          )}
        </Flex>
        {isAlarmActionsVisible && (
          <div className="event-footer-wrapper">
            <AlarmEventActionsRenderer
              location={location}
              event={event}
              onClose={this._onAlarmClose}
            />
          </div>
        )}
      </Grid>
    );
  }

  private focusEditNotesTextArea = () => {
    const { editNotesTextAreaRef } = this.props;
    editNotesTextAreaRef.current?.focus();
  };

  private _getFavouriteClass = () => {
    const { event } = this.props;
    const { favourite } = this.state;
    return classNames({
      favourite,
      'no-shadow': !EventUtil.hasCameras(event) && !favourite,
      'data-only': !EventUtil.hasCameras(event),
    });
  };

  private setCurrentScrollEvent = () => {
    const { dispatch, event } = this.props;
    dispatch(setCurrentScrollEvent(event.id));
  };

  private toggleEventFavouriteMenu = () => {
    this._toggleEventFavouriteHelper(true);
  };

  private _toggleEventFavourite = () => {
    this._toggleEventFavouriteHelper(false);
  };

  private _toggleEventFavouriteHelper = (viaMenu: boolean) => {
    const { event, locale, updateEvent, user } = this.props;
    const { favourite, favouritedBy, eventTitle, eventDescription, subtitle } = this.state;

    if (!user) {
      return;
    }

    const fav = !favourite;
    const favBy = favouritedBy;
    const favIndex = favBy.indexOf(user.sub);

    if (viaMenu) {
      AnalyticsLogger.clickEventMenuFavourite({ event, isStarred: fav });
    } else {
      AnalyticsLogger.toggleFavouriteEvent({ event, isEnabled: fav });
    }

    if (favIndex >= 0) {
      favBy.splice(favIndex, 1);
    } else {
      favBy.push(user.sub);
    }

    if (fav) {
      toast.success(locale.event.favourited, { autoClose: FAST_NOTIFICATION_DURATION });
    } else {
      toast.success(locale.event.unfavourited, { autoClose: FAST_NOTIFICATION_DURATION });
    }

    // TODO check if endpoint returns data and change accordingly

    EventActions.editEvent(event.id, {
      details: {
        description: eventDescription,
        favourite: fav,
      },
      title: eventTitle,
      subtitle,
      favouritedBy: favBy,
    }).then((editedEvent) => {
      updateEvent?.(editedEvent);
      this.setState({
        lastUpdatedBy: editedEvent.details.lastUpdatedBy,
        lastUpdateTime: editedEvent.details.lastUpdateTime,
      });
    });

    this.setState({
      favourite: fav,
      favouritedBy: favBy,
    });
  };

  private submitEdit = () => {
    const { eventDescription, favourite, eventTitle: title, subtitle } = this.state;
    const { event } = this.props;
    const eventDetails = event.details;

    const details: IEvent['details'] = { ...eventDetails };
    details.description = eventDescription;
    details.favourite = favourite;

    AnalyticsLogger.editNote({ event });

    EventActions.editEvent(event.id, {
      details,
      title,
      subtitle,
    }).then((editedEvent: IEvent) => {
      this.setState({
        eventDescription: editedEvent.details?.description || eventDescription,
        lastUpdatedBy: editedEvent.details?.lastUpdatedBy,
        lastUpdateTime: editedEvent.details?.lastUpdateTime,
      });
    });

    this.setState({
      editMode: false,
      eventDescriptionOriginal: eventDescription,
      eventTitleOriginal: title,
    });
  };

  private cancelEdit = () => {
    const { eventDescriptionOriginal, eventTitleOriginal } = this.state;

    this.setState({
      editMode: false,
      eventDescription: eventDescriptionOriginal,
      eventTitle: eventTitleOriginal,
    });
  };

  private updateEventDescription = ({ value }: { value: string }) =>
    this.setState({ eventDescription: value });

  private enterEditMode = () => {
    AnalyticsLogger.clickEventMenuChangeNote({ event: this.props.event });

    this.setState({
      editMode: true,
    });

    this.focusEditNotesTextArea();
  };

  private _renderUserDetails() {
    const { event, locale } = this.props;
    const { lastUpdatedBy, lastUpdateTime } = this.state;

    const { createdBy, updatedBy } = EventUtil.getCreatedByUpdatedByStrings({
      event,
      lastUpdatedBy,
      lastUpdateTime,
      locale,
    });

    if (createdBy || updatedBy) {
      return (
        <div className="user-details-panel" data-testid="user-detail-panel">
          {createdBy && <div>{createdBy}</div>}
          {updatedBy && <div>{updatedBy}</div>}
        </div>
      );
    }
  }

  private _renderReceipt(locationTimezone: string) {
    const { slideIndex } = this.state;
    const {
      event,
      onEventRowClick,
      searchString,
      searchStringRegex,
      searchCapsules,
      playingCameras,
      layoutType,
      colorMode,
    } = this.props;

    const sr = new SystemReport().report;
    let fontSize: string | undefined;
    if (sr.os.name === 'OS X') {
      fontSize = '0.93em';
    }

    const receiptClass = classNames('tab', {
      active: slideIndex === 0,
    });

    const cloudClass = classNames('tab', {
      active: slideIndex === 1,
    });

    return (
      <div className="event-detail-panel" data-testid="event-detail-panel">
        {event.videos && event.videos.length > 0 && (
          <div className="tabs">
            <span className={receiptClass} onClick={() => this._handleTapChange(0)}>
              <MDIIcon icon={CloudUploadIcon} size={18} />,
            </span>
            <span className={cloudClass} onClick={() => this._handleTapChange(1)}>
              <MDIIcon icon={ReceiptIcon} size={18} />,
            </span>
          </div>
        )}
        <EventDetails
          event={event}
          fontSize={fontSize}
          onContextMenu={this._handleContextMenu}
          onEventRowClick={onEventRowClick}
          searchCapsules={searchCapsules}
          searchString={searchString}
          searchStringRegex={searchStringRegex}
          timezone={locationTimezone}
          isVideoPlaying={playingCameras.length !== 0}
          layoutType={layoutType}
          colorMode={colorMode}
        />
        <EventVideos videos={event.videos} />
      </div>
    );
  }

  private _closeScheduledEvent = () => {
    const { event, updateEvent, cancelScheduledEvent, locale } = this.props;
    return cancelScheduledEvent({
      event: event as FutureEventEntity,
      updateEvent,
      successMessage: locale.event.scheduledCanceled,
    });
  };

  private _reopenScheduledEvent = () => {
    if (!this.props.updateEvent) {
      throw new Error('updateEvent is required to re-open scheduled event');
    }
    this.props.reopenScheduledEvent(this.props.updateEvent);
  };

  private _onAlarmClose = () => {
    this.setState({ isAlarmOpen: false });
  };

  private _handleTapChange = (value: number) => {
    this.setState({
      slideIndex: value,
    });
  };

  private _renderDescription({
    shouldRenderDownloadTitle,
  }: {
    shouldRenderDownloadTitle: boolean;
  }) {
    const { eventDescription, eventTitle } = this.state;
    const descriptionTextElement = this._highlightSearchStringDescription(eventDescription);

    return (
      <div>
        {shouldRenderDownloadTitle && (
          <div className="download-title">
            {this._highlightSearchStringDescription(eventTitle || '')}
          </div>
        )}
        {eventDescription && (
          <div className="description-text">
            <span className="dash">-</span>
            {descriptionTextElement}
          </div>
        )}
      </div>
    );
  }

  private _highlightSearchStringDescription(str: string) {
    const { searchString, searchStringRegex } = this.props;

    if (!searchString || !searchStringRegex) {
      return <div className="no-match">{str}</div>;
    }

    const results = str.split(searchStringRegex);

    return (
      <div>
        {results.map((result, index) => {
          if ((index & 1) === 1) {
            // all matched elements are at the odd position
            return (
              <span className="highlight" key={`description-highlight-${index}`}>
                {result}
              </span>
            );
          }
          return result;
        })}
      </div>
    );
  }

  private _onPlayEvent = (vmsCameraId?: string) => {
    const { event, isContextMenuOpen, onEventRowClick } = this.props;

    if (isContextMenuOpen || !onEventRowClick) {
      return;
    }

    const playAllCameras = !vmsCameraId;

    const { startTime } = event;
    onEventRowClick(event, vmsCameraId, new Date(startTime).valueOf(), playAllCameras);
  };

  private _handleMouseHover = () => {
    const {
      event: { startTime },
    } = this.props;

    EventItemActions.handleEventHoverActions({
      hoverDate: new Date(startTime),
    });
  };

  private _handleReceiptClick() {
    const selObj = window.getSelection();
    const str = selObj?.toString();
    return str;
  }

  private _handleContextMenu = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    detailsField?: { name: string; value: FieldValue; i18n: string },
  ) => {
    const { updateContextMenuStatus } = this.props;

    event.preventDefault();

    if (isKiosk()) {
      return;
    }

    const built = detailsField
      ? this._makeDetailsSearchUrls(detailsField)
      : this._makeFullTextSearchUrls();
    const { replaceSearchUrl, replaceSearchText, appendSearchUrl, appendSearchText } = built ?? {};

    if (!replaceSearchUrl || !appendSearchUrl || !replaceSearchText || !appendSearchText) {
      return;
    }

    const textOption = document.getElementById('text-option');

    if (textOption) {
      textOption.style.cssText = `opacity: 1; pointer-events: all; transform: translate(${
        event.pageX + 5
      }px, ${event.pageY + 5}px)`;

      const replaceSearchElement = `<div><a href='${replaceSearchUrl}' target='_blank' >${replaceSearchText}</a></div>`;
      const appendSearchElement = `<div><a href='${appendSearchUrl}' target='_blank' >${appendSearchText}</a></div>`;

      textOption.innerHTML = `${replaceSearchElement}${appendSearchElement}`;
    }

    // Listen for clicks, to dismiss contextual menu
    document.addEventListener('click', this._dismissContextMenu);
    // Set the contex menu state to open
    updateContextMenuStatus({ isOpen: true });
  };

  private _searchContext() {
    const {
      eventFilters,
      filterDate,
      selectedLocationIds,
      locale,
      searchString,
      searchCapsules,
      searchOperator,
      selectedFilterId,
      selectedReportName,
      tenantId,
      devicesBySiteId,
    } = this.props;
    return {
      origin: window.location.origin,
      eventFilters,
      filterDate,
      locationIds: selectedLocationIds,
      locale,
      searchString,
      searchCapsules,
      searchOperator,
      selectedFilterId,
      selectedReportName,
      tenantId,
      devicesBySiteId,
    };
  }

  private _makeFullTextSearchUrls() {
    const str = this._handleReceiptClick()?.trim();
    if (!str) return undefined;
    return buildEventSearchUrls({ text: str }, this._searchContext());
  }

  private _makeDetailsSearchUrls(capsule: { name: string; value: FieldValue; i18n: string }) {
    return buildEventSearchUrls({ capsule }, this._searchContext());
  }

  private _dismissContextMenu = () => {
    const { updateContextMenuStatus } = this.props;

    const textOption = document.getElementById('text-option');
    if (textOption) {
      textOption.innerHTML = '';
      textOption.style.cssText = '';
    }
    // Remove the listener for this action
    document.removeEventListener('click', this._dismissContextMenu);
    // Clear the context menu state
    updateContextMenuStatus({ isOpen: false });
  };
}

const ComposedEvent = forwardRef<HTMLDivElement, EventProps>(function ComposedEvent(props, ref) {
  const {
    authData: { user, contextineCreds, contextineEnabled },
    shared: { isContextMenuOpen },
    updateContextMenuStatus,
    locations: { loading: isLoadingLocations },
  } = useContext(CommonContext);

  const feature = useCurrentFeature();
  const dispatch = useDispatch();
  const store = useStore();
  const { date, range, locationIds, timezoneId } = useSelector((state) => state.filter);
  const locale = useLocale();
  const { customerId: tenantId, s3 } = useUserSession();
  const [preferences] = useUserPreferences();

  const { devicesBySiteId } = useDevices();

  const editNotesTextAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const setEditNotesTextAreaRef = useCallback(
    (elementRef: HTMLTextAreaElement) => (editNotesTextAreaRef.current = elementRef),
    [editNotesTextAreaRef],
  );

  const isEditNotesTextAreaFocused = useIsElementFocused(editNotesTextAreaRef);

  const playingCamerasController = usePlayingCamerasContext();

  const { playingCameras, playingCameraIds } = useControllerState(
    playingCamerasController.state,
    (state) => ({
      playingCameras: playingCamerasController.getPlayingCameras(),
      playingCameraIds: state.playingCameraIds,
    }),
    isShallowEqual,
  );

  const currentScrollEventId = useSelector(
    (state) => state.currentScrollEvent.currentScrollEventId,
  );
  const location = useFluxStore(
    LocationStore,
    (store) => props.event.siteId && store.getLocation(props.event.siteId),
  );

  const { cancelScheduledEvent, reopenScheduledEvent, isLoading } = useScheduledEventForm(
    props.event as FutureEventEntity,
  );
  const camerasDictionary = useFluxStore(LocationStore, (store) =>
    store.getAllLocationCamerasDict(),
  );

  const { observeElement: observeEventContent, isScrollable: isEventContentScrollable } =
    useIsElementScrollable();

  const verificationWorkflow = useEventFlow(props.event, props.workflows);

  const { read: canViewAgentFindings } = useAgentPermissions();
  const hasAiAgentFindingsLimited = useUserHasFeature('aiAgentFindingsLimited');
  const canViewLimitedAgentFindings = hasAiAgentFindingsLimited && !canViewAgentFindings;

  return (
    <Event
      {...props}
      forwardedRef={ref}
      contextineCreds={contextineCreds}
      contextineEnabled={contextineEnabled}
      dispatch={dispatch}
      filterDate={date}
      locale={locale}
      store={store}
      user={user}
      tenantId={tenantId}
      updateContextMenuStatus={updateContextMenuStatus}
      histogramRange={range}
      isContextMenuOpen={isContextMenuOpen}
      isLoadingLocations={isLoadingLocations}
      playingCameras={playingCameras}
      playingCameraIds={playingCameraIds}
      s3={s3}
      selectedLocationIds={locationIds}
      selectedLocationsTimezoneId={timezoneId}
      currentScrollEventId={currentScrollEventId}
      cancelScheduledEvent={cancelScheduledEvent}
      reopenScheduledEvent={reopenScheduledEvent}
      isLoadingScheduledEvent={isLoading}
      location={location}
      feature={feature}
      preferences={preferences}
      camerasDictionary={camerasDictionary}
      isEventContentScrollable={isEventContentScrollable}
      observeEventContent={observeEventContent}
      setEditNotesTextAreaRef={setEditNotesTextAreaRef}
      isEditNotesTextAreaFocused={isEditNotesTextAreaFocused}
      editNotesTextAreaRef={editNotesTextAreaRef}
      verificationWorkflow={verificationWorkflow}
      devicesBySiteId={devicesBySiteId}
      canViewAgentFindings={canViewAgentFindings || canViewLimitedAgentFindings}
      limitedAgentFindingsView={canViewLimitedAgentFindings}
    />
  );
});

export default React.memo(ComposedEvent);
